<!--
  A "Top 5" card. This is a port of CGS2022 analysisTopCard.vue: the first `limit` rows as the
  API ordered them, with Units and Volume (each with its trend, matched by displayProp), and %
  = units / totalUnits.

  Differences from CGS, on purpose:
    - `bars` draws a units bar per row (used for By Milestone and Top States).
    - The trend shows on hover (TrendDelta) instead of a click-to-toggle tooltip.
    - Volume is shown in whole dollars. CGS shows cents.
  `extra` adds columns, e.g. Investor/Shipping's Avg DOL:
    [{ label, value: row => …, format, trend: row => prev, lowerIsBetter }]
-->
<template>
  <InsightCard :title="title" :subtitle="subtitle" :loading="loading" :empty="!rows.length" :drillable="drillable" :drill-label="`View all ${title.toLowerCase()}`" @drill="$emit('drill')">
    <div class="ins-card__scroll">
      <table class="ins-table">
        <thead>
          <tr>
            <th scope="col">{{ nameHeader }}</th>
            <th v-if="bars" scope="col" class="ins-bar-col"><span class="ins-sr">Share</span></th>
            <th scope="col">Units</th>
            <th scope="col">Volume</th>
            <th v-if="showShare" scope="col">%</th>
            <th v-for="x in extra" :key="x.label" scope="col">{{ x.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="String(r.name)">
            <td>{{ r.name || '-' }}</td>
            <td v-if="bars" class="ins-bar-col"><div class="ins-bar"><span :style="{ width: r.barPct + '%' }"></span></div></td>
            <td>{{ fmtInt(r.count) }} <TrendDelta v-if="showTrend" :cur="r.count" :prev="r.trendCount" :show-pct="false" label="Trend units" /></td>
            <td>{{ fmtCurrency(r.volume) }} <TrendDelta v-if="showTrend" :cur="r.volume" :prev="r.trendVolume" format="cur" :show-pct="false" label="Trend volume" /></td>
            <td v-if="showShare">{{ r.share === null ? '0%' : r.share.toFixed(2) + '%' }}</td>
            <td v-for="x in extra" :key="x.label">
              {{ formatAs(x.value(r), x.format) }}
              <TrendDelta v-if="showTrend && x.trend" :cur="x.value(r)" :prev="x.trend(r)" :format="x.format" :lower-is-better="x.lowerIsBetter" :show-pct="false" :label="'Trend ' + x.label" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </InsightCard>
</template>

<script>
import InsightCard from './InsightCard.vue'
import TrendDelta from './TrendDelta.vue'
import { topWithTrend, withBars, fmtInt, fmtCurrency, formatAs } from '@/helpers/insights/insightMath.js'

export default {
  name: 'TopListCard',
  components: { InsightCard, TrendDelta },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    nameHeader: { type: String, default: 'Name' },
    items: { type: Array, default: () => [] },
    trendItems: { type: Array, default: () => [] },
    displayProp: { type: String, required: true },
    totalUnits: { type: [Number, String], default: null },
    limit: { type: Number, default: 5 },
    bars: { type: Boolean, default: false },
    showShare: { type: Boolean, default: true },
    extra: { type: Array, default: () => [] },
    // Off where the page's trend query is the same as its main one (Active Pipeline), which
    // would show every row as unchanged.
    showTrend: { type: Boolean, default: true },
    drillable: { type: Boolean, default: true },
    loading: { type: Boolean, default: false }
  },
  emits: ['drill'],
  computed: {
    rows() {
      const top = topWithTrend(this.items, this.trendItems, this.displayProp, { limit: this.limit, totalUnits: this.totalUnits })
      return this.bars ? withBars(top) : top
    }
  },
  methods: { fmtInt, fmtCurrency, formatAs }
}
</script>

<style scoped>
.ins-bar-col { width: 32%; min-width: 80px; }
.ins-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
</style>
